import React, { useState } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart, registerables, type ChartOptions } from 'chart.js';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  useTheme,
  Button,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
// import { MOCK_FOCUS_GROUPS } from '../data/focus_groups_sessions';
// import focusGroupsResponses from '../data/focus_group_responses.json';
import { 
  FocusGroupInsightsProps, 
  QuestionResponseType, 
  FocusGroupProcessedData,
  FocusGroupSessionResponseSchema,
  FocusGroupSessionSchema
} from '../data/types';
import { useFocusGroupSessionResponses, useFocusGroupSessions } from '../hooks/communityhook';

Chart.register(...registerables);

const chartColors = [
  'rgba(75, 192, 192, 0.6)',
  'rgba(255, 99, 132, 0.6)',
  'rgba(255, 206, 86, 0.6)',
  'rgba(54, 162, 235, 0.6)',
  'rgba(153, 102, 255, 0.6)',
];

// interface SlideshowViewProps {
//   sectionData: Array<{
//     question: {
//       id: string;
//       title: string;
//       text: string;
//       type: string;
//       options?: string[];
//     };
//     data: FocusGroupProcessedData | null;
//   }>;
//   section: {
//     id: string;
//     title: string;
//     icon?: string;
//   };
// }

// const SlideshowView: React.FC<SlideshowViewProps> = ({ sectionData, section }) => {
//   const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
//   const theme = useTheme();

//   // Filter out items with null data
//   const validSectionData = sectionData.filter(item => item.data !== null);
//   if (validSectionData.length === 0) return null;

//   const handleNext = () => {
//     setCurrentQuestionIndex((prev) => 
//       prev < validSectionData.length - 1 ? prev + 1 : prev
//     );
//   };

//   const handlePrevious = () => {
//     setCurrentQuestionIndex((prev) => 
//       prev > 0 ? prev - 1 : prev
//     );
//   };

//   const { question, data } = validSectionData[currentQuestionIndex];
  
//   const chartData = {
//     labels: (data as FocusGroupProcessedData).labels,
//     datasets: [{
//       label: question.title,
//       data: (data as FocusGroupProcessedData).data,
//       backgroundColor: chartColors[currentQuestionIndex % chartColors.length],
//       borderColor: chartColors[currentQuestionIndex % chartColors.length].replace('0.6', '1'),
//       borderWidth: 1,
//     }],
//   };

//   const options = {
//     responsive: true,
//     scales: {
//       y: {
//         beginAtZero: true,
//       },
//     },
//   };

//   return (
//     <Box sx={{ position: 'relative', minHeight: 400 }}>
//       <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//         <Typography variant="subtitle2" gutterBottom>
//           {question.text}
//         </Typography>
//         <Box>
//           <IconButton 
//             onClick={handlePrevious} 
//             disabled={currentQuestionIndex === 0}
//             sx={{ color: theme.palette.primary.main }}
//           >
//             <NavigateBeforeIcon />
//           </IconButton>
//           <IconButton 
//             onClick={handleNext} 
//             disabled={currentQuestionIndex === validSectionData.length - 1}
//             sx={{ color: theme.palette.primary.main }}
//           >
//             <NavigateNextIcon />
//           </IconButton>
//         </Box>
//       </Box>
//       <Bar data={chartData} options={options} />
//       <Box sx={{ mt: 2, textAlign: 'center' }}>
//         <Typography variant="caption" color="textSecondary">
//           Question {currentQuestionIndex + 1} of {validSectionData.length}
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

interface SectionViewProps {
  section: {
    id: string;
    title: string;
    icon?: string;
  };
  sectionData: Array<{
    question: {
      id: string;
      title: string;
      text: string;
      type: string;
      options?: string[];
    };
    data: FocusGroupProcessedData | null;
  }>;
  pastSessionResponses: FocusGroupSessionResponseSchema[];
}

const SectionView: React.FC<SectionViewProps> = ({ section, sectionData, pastSessionResponses }) => {
  // Calculate section statistics
//   const totalResponses = sectionData.reduce((acc, { data }) => 
//     acc + (data ? data.data.reduce((sum, val) => sum + val, 0) : 0), 0);
  
  // Get total participants from pastSessionResponses
  const totalParticipants = pastSessionResponses.length;
  
  // Calculate how many participants responded to at least one question in this section
  const participantsWhoResponded = pastSessionResponses.filter(response => {
    // Check if the participant answered any question in this section
    return sectionData.some(({ question }) => {
      const answer = response[question.id];
      return answer !== undefined && answer !== null && 
        // For arrays (checkbox), ensure at least one option selected
        (Array.isArray(answer) ? answer.length > 0 : true) &&
        // For objects (rating), ensure at least one rating given
        (typeof answer === 'object' && !Array.isArray(answer) ? Object.keys(answer).length > 0 : true);
    });
  }).length;
  
  const participationRate = totalParticipants > 0 ? 
    Math.min(Math.round((participantsWhoResponded / totalParticipants) * 100), 100) : 0;
  
//   const responseCounts = sectionData.map(({ question, data }) => ({
//     questionId: question.id,
//     questionText: question.text,
//     totalResponses: data ? data.data.reduce((sum, val) => sum + val, 0) : 0
//   }));

  // Calculate insights for pie chart based on actual responses
  const responseBreakdown = sectionData.reduce((acc, { question, data }) => {
    if (!data) return acc;
    
    // For checkbox questions, count each selected option
    if (question.type === 'checkbox') {
      data.labels.forEach((label, index) => {
        acc[label] = (acc[label] || 0) + data.data[index];
      });
    }
    // For rating questions, calculate average rating
    else if (question.type === 'rating') {
      data.labels.forEach((label, index) => {
        acc[label] = (acc[label] || 0) + data.data[index];
      });
    }
    return acc;
  }, {} as Record<string, number>);

  // Sort the breakdown by value and take top 5
  const sortedBreakdown = Object.entries(responseBreakdown)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const pieChartData = {
    labels: sortedBreakdown.map(([label]) => label),
    datasets: [{
      data: sortedBreakdown.map(([, value]) => value),
      backgroundColor: chartColors.slice(0, sortedBreakdown.length),
      borderColor: chartColors.slice(0, sortedBreakdown.length).map(color => color.replace('0.6', '1')),
      borderWidth: 1,
    }],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const total: number = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage: number = Math.round((context.raw / total) * 100);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        formatter: (value: number, ctx: any) => {
          const total: number = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage: number = Math.round((value / total) * 100);
          return `${percentage}%`;
        },
        color: '#fff',
        font: {
          weight: 'bold',
          size: 12
        }
      }
    }
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context: any) {
            const total: number = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage: number = Math.round((context.raw / total) * 100);
            return `${context.raw} (${percentage}%)`;
          }
        }
      },
    } as any,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false
        },
        border: { display: false },
        ticks: {
          font: {
            size: 12
          },
          padding: 8,
          callback: function(value: any) {
            return value + ' ';
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        border: { display: false },
        ticks: {
          font: {
            size: 12,
            weight: 500
          },
          padding: 8,
          maxRotation: 0,
          minRotation: 0
        }
      }
    },
    layout: {
      padding: {
        top: 40,
        right: 20,
        bottom: 20,
        left: 20
      }
    }
  };

  return (
    <Card 
      elevation={2}
      sx={{
        height: '100%',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: (theme) => theme.shadows[4],
        }
      }}
    >
      <CardContent>
        <p className="text-primary-400/80 font-open-sans text-lg font-medium">
          {section.title}
        </p>

        {/* General Section Data */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            Section Overview
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Total Participants
              </Typography>
              <Typography variant="h6">
                {totalParticipants}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Total Questions
              </Typography>
              <Typography variant="h6">
                {sectionData.length}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Participation Rate
              </Typography>
              <Typography variant="h6">
                {participationRate}%
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Response Distribution */}
        <Box 
          sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: 'background.paper', 
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
        >
          <Box 
            sx={{ 
              width: '100%',
              height: 280,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative'
            }}
          >
            <Pie data={pieChartData} options={pieChartOptions} />
          </Box>
        </Box>

        {/* Response Counts */}
        {/* <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            Response Counts
          </Typography>
          <Grid container spacing={2}>
            {responseCounts.map(({ questionId, questionText, totalResponses }) => (
              <Grid item xs={12} sm={6} key={questionId}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {questionText}
                </Typography>
                <Typography variant="h6">
                  {totalResponses}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box> */}

        {/* Question Charts */}
        <Box>
          {sectionData.map(({ question, data }, index) => {
            if (!data) return null;

            const chartData = {
              labels: data.labels,
              datasets: [{
                label: question.title,
                data: data.data,
                backgroundColor: chartColors[index % chartColors.length],
                borderColor: chartColors[index % chartColors.length].replace('0.6', '1'),
                borderWidth: 1,
              }],
            };

            return (
              <Box key={question.id} sx={{ mb: 4 }}>
                <p className='font-semibold text-md text-primary-200 mb-4'>
                  {question.text}
                </p>
                <Box sx={{ 
                  height: 300,
                  position: 'relative',
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  p: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <Bar data={chartData} options={options} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

const FocusGroupInsights: React.FC<FocusGroupInsightsProps> = ({ sessionId }) => {
  const { pastSessionResponses } = useFocusGroupSessionResponses(sessionId);
  const [viewMode, setViewMode] = useState<'grid' | 'slideshow'>('slideshow');
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();

  const { formattedFocusGroupSessions } = useFocusGroupSessions()
  const session = formattedFocusGroupSessions.find(s => s.id === sessionId);
  if (!session) return null;

  const processSectionResponses = (sectionId: string) => {
    const section = session.sections.find(s => s.id === sectionId);
    if (!section || !pastSessionResponses || !(section?.questions)) return null;

    return section.questions.map(question => {
      const responses = pastSessionResponses.map(r => r[question.id] as QuestionResponseType);
      const processedData = processQuestionResponses(question, responses);
      return {
        question,
        data: processedData
      };
    });
  };

  const processQuestionResponses = (question: any, responses: QuestionResponseType[]): FocusGroupProcessedData | null => {
    if (question.type === 'checkbox') {
      const optionCounts: Record<string, number> = {};
      
      // Initialize counts for predefined options
      question.options.forEach((opt: string) => {
        optionCounts[opt] = 0;
      });

      // Process responses and count both predefined and custom options
      responses.forEach(response => {
        if (Array.isArray(response)) {
          response.forEach(opt => {
            // Increment count for both predefined and custom options
            optionCounts[opt] = (optionCounts[opt] || 0) + 1;
          });
        } else if (typeof response === 'object' && !Array.isArray(response)) {
          Object.entries(response).forEach(([opt, rating]) => {
            optionCounts[opt] = (optionCounts[opt] || 0) + 1;
          });
        } else if (typeof response === 'string') {
          optionCounts[response] = (optionCounts[response] || 0) + 1;
        }
      });

      delete optionCounts['Other']

      return {
        labels: Object.keys(optionCounts),
        data: Object.values(optionCounts)
      };
    } 
    else if (question.type === 'rating') {
      const optionAverages: Record<string, number> = {};
      const optionCounts: Record<string, number> = {};
      
      question.options.forEach((opt: string) => {
        optionAverages[opt] = 0;
        optionCounts[opt] = 0;
      });

      responses.forEach(response => {
        if (typeof response === 'object' && !Array.isArray(response)) {
          Object.entries(response).forEach(([opt, rating]) => {
            if (typeof rating === 'number') {
              optionAverages[opt] += rating;
              optionCounts[opt]++;
            }
          });
        }
      });

      Object.keys(optionAverages).forEach(opt => {
        if (optionCounts[opt] > 0) {
          optionAverages[opt] = optionAverages[opt] / optionCounts[opt];
        }
      });

      return {
        labels: Object.keys(optionAverages),
        data: Object.values(optionAverages)
      };
    }

    return null;
  };

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'grid' | 'slideshow',
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleNext = () => {
    setCurrentSectionIndex((prev) => 
      prev < session.sections.length - 1 ? prev + 1 : prev
    );
  };

  const handlePrevious = () => {
    setCurrentSectionIndex((prev) => 
      prev > 0 ? prev - 1 : prev
    );
  };

  const convertToCSV = (data: FocusGroupSessionResponseSchema[], session: FocusGroupSessionSchema): string => {
    if (!data || data.length === 0) return '';

    // Collect all question IDs from all sections
    const allQuestionIds = session.sections.flatMap((section: { questions: Array<{ id: string }> }) => 
      section.questions.map((question: { id: string }) => question.id)
    );

    // Create headers - metadata columns first, then all question IDs
    // Exclude username if session allows anonymous responses
    const headers = session.allowAnonymous 
      ? ['session_id', 'created', 'last_submitted', ...allQuestionIds]
      : ['username', 'session_id', 'created', 'last_submitted', ...allQuestionIds];

    // Create rows
    const rows = data.map(response => {
      const row: string[] = [];
      
      // Add metadata (exclude username if anonymous is allowed)
      if (!session.allowAnonymous) {
        row.push(response.username || '');
      }
      row.push(
        response.session_id || '',
        response.created || '',
        response.last_submitted || ''
      );

      // Add question responses
      allQuestionIds.forEach((questionId: string) => {
        const answer = response[questionId];
        let cellValue = '';

        if (answer === undefined || answer === null) {
          cellValue = '';
        } else if (Array.isArray(answer)) {
          // For checkbox responses (array), join with semicolon
          cellValue = answer.join('; ');
        } else if (typeof answer === 'object') {
          // For rating responses (object), format as "key: value" pairs
          const pairs = Object.entries(answer).map(([key, value]) => `${key}: ${value}`);
          cellValue = pairs.join('; ');
        } else {
          // For string responses
          cellValue = String(answer);
        }

        // Escape quotes and wrap in quotes if contains comma, semicolon, or newline
        if (cellValue.includes(',') || cellValue.includes(';') || cellValue.includes('\n') || cellValue.includes('"')) {
          cellValue = `"${cellValue.replace(/"/g, '""')}"`;
        }
        
        row.push(cellValue);
      });

      return row;
    });

    // Combine headers and rows
    const csvRows = [
      headers.map(h => h.includes(',') || h.includes('"') ? `"${h.replace(/"/g, '""')}"` : h).join(','),
      ...rows.map(row => row.join(','))
    ];

    return csvRows.join('\n');
  };

  const convertChartDataToCSV = (session: FocusGroupSessionSchema, pastSessionResponses: FocusGroupSessionResponseSchema[]): string => {
    const rows: string[] = [];
    
    // Header row
    rows.push('Section,Question,Question Type,Option/Value,Count,Percentage,Total Responses');
    
    // Process each section
    session.sections.forEach(section => {
      const sectionData = processSectionResponses(section.id);
      if (!sectionData) return;
      
      // Calculate section statistics
      const totalParticipants = pastSessionResponses.length;
      const participantsWhoResponded = pastSessionResponses.filter(response => {
        return sectionData.some(({ question }) => {
          const answer = response[question.id];
          return answer !== undefined && answer !== null && 
            (Array.isArray(answer) ? answer.length > 0 : true) &&
            (typeof answer === 'object' && !Array.isArray(answer) ? Object.keys(answer).length > 0 : true);
        });
      }).length;
      
      const participationRate = totalParticipants > 0 ? 
        Math.min(Math.round((participantsWhoResponded / totalParticipants) * 100), 100) : 0;
      
      // Add section overview row
      rows.push(`${section.title},Section Overview,Statistics,Total Participants,${totalParticipants},100%,${totalParticipants}`);
      rows.push(`${section.title},Section Overview,Statistics,Total Questions,${sectionData.length},100%,${sectionData.length}`);
      rows.push(`${section.title},Section Overview,Statistics,Participation Rate,${participationRate}%,100%,${totalParticipants}`);
      
      // Process each question's chart data
      sectionData.forEach(({ question, data }) => {
        if (!data) return;
        
        // Calculate total for this question
        const total = data.data.reduce((sum, val) => sum + val, 0);
        
        // Add rows for each option/value in the chart
        data.labels.forEach((label, index) => {
          const count = data.data[index];
          const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
          
          // Escape special characters in CSV
          const escapeCSV = (str: string) => {
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          };
          
          rows.push(
            `${escapeCSV(section.title)},${escapeCSV(question.text)},${question.type},${escapeCSV(label)},${count},${percentage}%,${total}`
          );
        });
      });
    });
    
    return rows.join('\n');
  };

  const handleDownloadCSV = () => {
    if (!pastSessionResponses || pastSessionResponses.length === 0) {
      alert('No response data available to download');
      return;
    }

    const csvContent = convertToCSV(pastSessionResponses, session);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${session.title.replace(/[^a-z0-9]/gi, '_')}_responses_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setDownloadMenuAnchor(null);
  };

  const handleDownloadChartData = () => {
    if (!pastSessionResponses || pastSessionResponses.length === 0) {
      alert('No response data available to download');
      return;
    }

    const csvContent = convertChartDataToCSV(session, pastSessionResponses);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${session.title.replace(/[^a-z0-9]/gi, '_')}_chart_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setDownloadMenuAnchor(null);
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <p className="text-primary-300 font-raleway text-xl font-semibold">
            {session.title}
          </p>
          <p className="text-gray-500 font-open-sans text-sm">
            {session.description}
          </p>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Download data">
            <Button
              className="text-primary-300 border-primary-300"
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon className="text-primary-300 border-primary-300" />}
              endIcon={<ArrowDropDownIcon className="text-primary-300 border-primary-300" />}
              onClick={handleDownloadMenuOpen}
              disabled={!pastSessionResponses || pastSessionResponses.length === 0}
            >
              <p className="text-primary-300 font-open-sans text-sm font-semibold">Download</p>
            </Button>
          </Tooltip>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={handleDownloadMenuClose}
          >
            <MenuItem onClick={handleDownloadCSV}>
              <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
              Download Session Responses
            </MenuItem>
            <MenuItem onClick={handleDownloadChartData}>
              <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
              Download Chart Data
            </MenuItem>
          </Menu>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="grid">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="slideshow">
              <ViewCarouselIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      
      {viewMode === 'grid' ? (
        <Grid container spacing={4}>
          {session.sections.map(section => {
            const sectionData = processSectionResponses(section.id);
            if (!sectionData) return null;

            return (
              <Grid item xs={12} md={6} key={section.id}>
                <SectionView 
                  section={section} 
                  sectionData={sectionData} 
                  pastSessionResponses={pastSessionResponses || []} 
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <IconButton 
              onClick={handlePrevious} 
              disabled={currentSectionIndex === 0}
              sx={{ color: theme.palette.primary.main }}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="caption" color="textSecondary">
              Section {currentSectionIndex + 1} of {session.sections.length}
            </Typography>
            <IconButton 
              onClick={handleNext} 
              disabled={currentSectionIndex === session.sections.length - 1}
              sx={{ color: theme.palette.primary.main }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
          {(() => {
            const section = session.sections[currentSectionIndex];
            const sectionData = processSectionResponses(section.id);
            if (!sectionData) return null;

            return (
              <SectionView 
                section={section} 
                sectionData={sectionData} 
                pastSessionResponses={pastSessionResponses || []} 
              />
            );
          })()}
        </Box>
      )}
    </Box>
  );
};

export default FocusGroupInsights; 